/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { NAV_PAGE_LIST, getSettingsTableColumns } from '../relationship/constants';
import { getRelationshipSettingsListAction, resetStateAction } from './actions';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import saga from './saga';
import { removeModalQuery } from '../../utils/removeModalQuery';
import {
	getIsRelationshipSettingsListLoadingSelector,
	getPaginationSelector,
	getRelationshipSettingsListSelector,
} from './selectors';
import { getIsAvailableRelationshipSettingsButtonsSelector } from '../profile/selectors';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';

const RelationshipSettingsContainerWrapper = ({
	onGetRelationshipSettingsList,
	onResetState,
	relationshipSettingsList,
	pagination,
	isRelationshipSettingsListLoading,
	isAvailable,
}) => {
	useInjectSaga({ key: 'relationshipSettingsContainerSaga', saga });
	useInjectReducer({ key: 'relationshipSettingsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetRelationshipSettingsList({ searchQuery: removeModalQuery(query.toString()) });

		return () => {
			onResetState();
		};
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'relationship_settings');
		query.set('mode', 'add');
		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetRelationshipSettingsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetRelationshipSettingsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetRelationshipSettingsList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, getSettingsTableColumns()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetRelationshipSettingsList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			pageContent={
				<DataGridComponent
					headerText="Настройки ТЭО"
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					columns={getSettingsTableColumns()}
					rows={relationshipSettingsList || []}
					pagination={
						pagination
							? { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
							: null
					}
					isTableDataLoading={isRelationshipSettingsListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	relationshipSettingsList: getRelationshipSettingsListSelector(),
	isRelationshipSettingsListLoading: getIsRelationshipSettingsListLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableRelationshipSettingsButtonsSelector(),
});

const mapDispatchToProps = {
	onGetRelationshipSettingsList: getRelationshipSettingsListAction,
	onResetState: resetStateAction,
};

const RelationshipSettingsContainer = connect(
	mapStateToProps,
	mapDispatchToProps,
)(RelationshipSettingsContainerWrapper);

RelationshipSettingsContainerWrapper.propTypes = {
	onGetRelationshipSettingsList: PropTypes.func.isRequired,
	relationshipSettingsList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isRelationshipSettingsListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default RelationshipSettingsContainer;
